import type { Message } from '@volt/shared';
import { nextSpeakable, toSpeakableText } from './speakable.js';
import { SpeechQueue } from './speechQueue.js';
import { DEFAULT_VAD_CONFIG, VoiceActivityDetector, rmsOfByteFrame } from './voiceActivity.js';
import { createThinkingCue, type ThinkingCue } from './voiceCues.js';
import { REACTION_SILENCE_MS, type VoiceSettings } from './voiceSettings.js';
import { createWakeLock, type WakeLockHandle } from './wakeLock.js';
import { getSharedAudio } from './audioUnlock.js';

export type VoiceStatus = 'starting' | 'listening' | 'transcribing' | 'thinking' | 'speaking' | 'error';

/** Error codes that make carrying on pointless (the user must act first). */
const FATAL_CODES = new Set([
  'MIC_DENIED',
  'MIC_UNAVAILABLE',
  'INSUFFICIENT_BALANCE',
  'BELOW_MIN_BALANCE',
  'MODEL_UNAVAILABLE',
  'DAILY_LIMIT_EXCEEDED',
  'UNAUTHORIZED',
  'USER_BANNED',
]);

const IDLE_END_MS = 180_000;
const TICK_MS = 50;
/** Holding the circle shorter than this is a stray tap, not a phrase. */
const MIN_HOLD_MS = 350;
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'];
const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};
/** Talking over the assistant must be clearly louder than room noise or its own echo. */
const BARGE_IN_VAD = { ...DEFAULT_VAD_CONFIG, minSpeechRms: 0.06, startMs: 350 };

export interface VoiceEngineApi {
  transcribeVoice(body: {
    dataBase64: string;
    mime: string;
    durationSeconds?: number;
    language?: string;
    quality?: 'standard' | 'high';
  }): Promise<{ text: string; filtered?: boolean }>;
  speakVoice(body: {
    text: string;
    voice?: string;
    tone?: 'neutral' | 'warm' | 'cheerful' | 'calm';
  }): Promise<{ audioBase64: string; mime: string }>;
}

export interface VoiceEngineCallbacks {
  onStatus(status: VoiceStatus): void;
  /** Microphone loudness 0..1 while listening (for the animation). */
  onLevel(level: number): void;
  onUserText(text: string): void;
  onAssistantText(text: string): void;
  /** Non-fatal problems are shown once and the conversation goes on; fatal ones end it. */
  onError(code: string, fatal: boolean): void;
  /** No speech for a long time: the screen should close itself. */
  onIdleTimeout(): void;
}

export interface VoiceEngineOptions {
  api: VoiceEngineApi;
  /** ISO-639-1 code: recogniser language hint and browser-voice language. */
  language: string;
  /** Read on every use, so a change in the settings panel applies to the running call. */
  getSettings(): VoiceSettings;
  /** Sends the recognised phrase into the chat as a voice-style turn. */
  sendText(text: string): void;
  /** Stops the model's generation (the user interrupted). */
  stopGeneration(): void;
  callbacks: VoiceEngineCallbacks;
  /** Test seams. */
  now?: () => number;
  readLevel?: () => number;
  wakeLock?: WakeLockHandle;
}

interface PendingReply {
  /** Assistant messages that existed before this turn: never the reply. */
  knownAssistantIds: Set<string>;
  consumed: number;
  /** Phrases handed to the speech queue so far; the first one is cut short for a faster start. */
  queued: number;
  sawStreaming: boolean;
  /** The whole answer was handed to the speech queue. */
  fullyQueued: boolean;
}

export class VoiceEngine {
  private status: VoiceStatus = 'starting';
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private recorder: MediaRecorder | null = null;
  private recorderMime = 'audio/webm';
  private chunks: Blob[] = [];
  private segmentStartedAt = 0;
  private vad: VoiceActivityDetector | null = null;
  private bargeVad: VoiceActivityDetector | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastActivityAt = Date.now();
  private pending: PendingReply | null = null;
  private latest: { messages: Message[]; streaming: boolean } = { messages: [], streaming: false };
  private turn = 0;
  private destroyed = false;
  private ttsErrorReported = false;
  private holding = false;
  private holdStartedAt = 0;
  private reacquiring = false;
  private cue: ThinkingCue | null = null;
  private readonly wakeLock: WakeLockHandle;
  private readonly queue: SpeechQueue<Blob>;

  constructor(private readonly options: VoiceEngineOptions) {
    this.wakeLock = options.wakeLock ?? createWakeLock();
    this.queue = new SpeechQueue<Blob>({
      synthesize: async (text) => {
        const { voice, tone } = this.settings;
        const speech = await options.api.speakVoice({ text, voice, tone });
        return base64ToBlob(speech.audioBase64, speech.mime);
      },
      play: (blob, signal) => playBlob(blob, signal, () => this.settings.speed),
      fallback: (text, signal) => speakWithBrowserVoice(text, options.language, this.settings.speed, signal),
      onStart: () => {
        this.cue?.stop();
        if (this.status === 'thinking') this.setStatus('speaking');
      },
      onIdle: () => this.onSpeechIdle(),
      onError: () => {
        // Falls back to the browser's voice; tell the user only once.
        if (!this.ttsErrorReported) {
          this.ttsErrorReported = true;
          options.callbacks.onError('VOICE_TTS_FALLBACK', false);
        }
      },
    });
  }

  private get settings(): VoiceSettings {
    return this.options.getSettings();
  }

  private now(): number {
    return this.options.now ? this.options.now() : performance.now();
  }

  async start(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this.fail('MIC_UNAVAILABLE');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
    } catch {
      this.fail('MIC_DENIED');
      return;
    }
    if (this.destroyed) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctx) {
      this.audioContext = new Ctx();
      void this.audioContext.resume().catch(() => undefined);
      // iOS suspends the context on a call or a locked screen; bring it back as soon as it is allowed.
      this.audioContext.onstatechange = () => {
        const state = this.audioContext?.state as string | undefined;
        if (state === 'suspended' || state === 'interrupted') void this.audioContext?.resume().catch(() => undefined);
      };
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 1024;
      this.cue = createThinkingCue(this.audioContext);
    }
    this.attachStream(stream);
    this.recorderMime = MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
    document.addEventListener('visibilitychange', this.onVisibility);
    void this.wakeLock.acquire();
    this.timer = setInterval(() => this.tick(), TICK_MS);
    this.beginListening();
  }

  /** Feed every chat update here; speaks the reply as it is written. */
  observe(messages: Message[], streaming: boolean): void {
    this.latest = { messages, streaming };
    const pending = this.pending;
    if (!pending || this.destroyed) return;
    if (streaming) pending.sawStreaming = true;

    const reply = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant' && !pending.knownAssistantIds.has(message.id));

    if (!reply) {
      // The request failed before an answer even started.
      if (pending.sawStreaming && !streaming) this.endTurnWithError('PROVIDER_ERROR');
      return;
    }
    if (reply.status === 'failed') {
      this.endTurnWithError(reply.errorMessage === 'INSUFFICIENT_BALANCE' ? 'INSUFFICIENT_BALANCE' : 'PROVIDER_ERROR');
      return;
    }

    const done = !streaming && reply.status !== 'streaming' && reply.status !== 'pending';
    const step = nextSpeakable(reply.content, pending.consumed, done, { eager: pending.queued === 0 });
    pending.consumed = step.consumed;
    for (const chunk of step.chunks) {
      pending.queued += 1;
      this.queue.enqueue(chunk);
    }
    this.options.callbacks.onAssistantText(toSpeakableText(reply.content));

    if (done) {
      pending.fullyQueued = true;
      if (!this.queue.busy) this.onSpeechIdle();
    }
  }

  /** The user cuts the assistant off (tap): stop talking and generating, listen again. */
  interrupt(): void {
    if (this.destroyed) return;
    this.turn += 1; // a transcription still in flight must not start a new turn
    this.cue?.stop();
    this.queue.interrupt();
    getSharedAudio().pause();
    window.speechSynthesis?.cancel();
    if (this.latest.streaming) this.options.stopGeneration();
    this.pending = null;
    this.beginListening();
  }

  /** Settings changed in the panel: re-read the ones that shape listening, apply speed to what is playing. */
  settingsChanged(): void {
    if (this.destroyed) return;
    getSharedAudio().playbackRate = this.settings.speed;
    if (this.status === 'listening' && !this.holding) this.beginListening();
  }

  /** Push-to-talk: the circle is pressed. Cuts the assistant off first if it is talking. */
  pressToTalk(): void {
    if (this.destroyed || this.settings.talkMode !== 'push') return;
    if (this.status === 'speaking' || this.status === 'thinking') this.interrupt();
    if (this.status !== 'listening' || this.holding) return;
    this.holding = true;
    this.holdStartedAt = this.now();
    this.lastActivityAt = Date.now();
    this.startRecorder();
  }

  /** Push-to-talk: the circle is released. Sends the phrase, or drops an accidental tap. */
  releaseToTalk(): void {
    if (!this.holding) return;
    this.holding = false;
    if (this.now() - this.holdStartedAt < MIN_HOLD_MS) {
      this.discardRecorder();
      return;
    }
    void this.finishUtterance();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.cue?.stop();
    this.queue.interrupt();
    getSharedAudio().pause();
    window.speechSynthesis?.cancel();
    if (this.pending && this.latest.streaming) this.options.stopGeneration();
    this.pending = null;
    this.releaseMedia();
  }

  // ── listening ────────────────────────────────────────────────────────
  private beginListening(): void {
    if (this.destroyed || !this.stream) return;
    this.lastActivityAt = Date.now();
    this.holding = false;
    this.cue?.stop();
    this.setStatus('listening');
    this.bargeVad = null;
    const { talkMode, reaction } = this.settings;
    this.vad = new VoiceActivityDetector(this.now(), { ...DEFAULT_VAD_CONFIG, silenceMs: REACTION_SILENCE_MS[reaction] });
    // Hands-free records continuously and cuts at pauses; push-to-talk records only while held.
    if (talkMode === 'auto') this.restartRecorder();
    else this.discardRecorder();
  }

  private tick(): void {
    if (this.destroyed || !this.stream) return;
    if (this.status === 'listening') {
      this.tickListening();
    } else if ((this.status === 'speaking' || this.status === 'thinking') && this.settings.bargeIn && this.settings.talkMode === 'auto') {
      this.tickBargeIn();
    }
  }

  private tickListening(): void {
    const now = this.now();
    const rms = this.readLevel();
    this.options.callbacks.onLevel(Math.min(1, rms * 6));

    if (this.settings.talkMode === 'auto' && this.vad) {
      const event = this.vad.push(rms, now);
      if (event === 'speech-started') this.lastActivityAt = Date.now();
      else if (event === 'rotate' || event === 'discard') {
        this.restartRecorder();
        this.vad.reset(now);
      } else if (event === 'speech-ended') {
        void this.finishUtterance();
        return;
      }
    }
    if (Date.now() - this.lastActivityAt > IDLE_END_MS) this.options.callbacks.onIdleTimeout();
  }

  /** The user starts talking over the assistant: stop it and carry on as a normal phrase. */
  private tickBargeIn(): void {
    const now = this.now();
    this.bargeVad ??= new VoiceActivityDetector(now, BARGE_IN_VAD);
    if (this.bargeVad.push(this.readLevel(), now) !== 'speech-started') return;
    this.interrupt();
    // Their first words were spoken before the recorder restarted: treat the phrase as already under way.
    this.vad?.forceSpeech(this.now(), this.now() - BARGE_IN_VAD.startMs);
  }

  private readLevel(): number {
    if (this.options.readLevel) return this.options.readLevel();
    if (!this.analyser) return 0;
    const frame = new Uint8Array(this.analyser.fftSize);
    this.analyser.getByteTimeDomainData(frame);
    return rmsOfByteFrame(frame);
  }

  private startRecorder(): void {
    if (!this.stream) return;
    this.chunks = [];
    const recorder = this.recorderMime
      ? new MediaRecorder(this.stream, { mimeType: this.recorderMime })
      : new MediaRecorder(this.stream);
    recorder.ondataavailable = (event) => {
      if (event.data.size) this.chunks.push(event.data);
    };
    recorder.start();
    this.recorder = recorder;
    this.segmentStartedAt = this.now();
  }

  private restartRecorder(): void {
    this.discardRecorder();
    this.startRecorder();
  }

  private discardRecorder(): void {
    const old = this.recorder;
    if (old && old.state !== 'inactive') {
      old.ondataavailable = null;
      old.onstop = null;
      old.stop();
    }
    this.recorder = null;
    this.chunks = [];
  }

  private async finishUtterance(): Promise<void> {
    const recorder = this.recorder;
    if (!recorder) return;
    this.setStatus('transcribing');
    const durationSeconds = (this.now() - this.segmentStartedAt) / 1000;
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () =>
        resolve(new Blob(this.chunks, { type: (recorder.mimeType || this.recorderMime || 'audio/webm').split(';')[0] }));
      if (recorder.state !== 'inactive') recorder.stop();
      else recorder.onstop?.(new Event('stop'));
    });
    this.recorder = null;
    if (this.destroyed) return;
    if (blob.size === 0) {
      this.beginListening();
      return;
    }

    const turn = ++this.turn;
    try {
      const { text, filtered } = await this.options.api.transcribeVoice({
        dataBase64: await blobToBase64(blob),
        mime: blob.type || 'audio/webm',
        durationSeconds: Math.round(durationSeconds * 10) / 10,
        language: this.options.language,
        quality: this.settings.recognition,
      });
      if (this.destroyed || turn !== this.turn) return;
      const phrase = text.trim();
      // Silence, a stray sound, or a phantom phrase the server threw away: nothing to answer.
      if (filtered || phrase.replace(/[\s.,!?…\-—]/g, '').length < 2) {
        this.beginListening();
        return;
      }
      this.submitPhrase(phrase);
    } catch (error) {
      if (this.destroyed || turn !== this.turn) return;
      this.handleApiError(error);
    }
  }

  // ── turn lifecycle ───────────────────────────────────────────────────
  /** Hands a recognised phrase to the chat and starts waiting for the spoken answer. */
  submitPhrase(phrase: string): void {
    if (this.destroyed) return;
    this.options.callbacks.onUserText(phrase);
    this.options.callbacks.onAssistantText('');
    this.pending = {
      knownAssistantIds: new Set(
        this.latest.messages.filter((message) => message.role === 'assistant').map((message) => message.id),
      ),
      consumed: 0,
      queued: 0,
      sawStreaming: false,
      fullyQueued: false,
    };
    this.setStatus('thinking');
    this.bargeVad = null;
    if (this.settings.cues) this.cue?.start();
    this.options.sendText(phrase);
  }

  private onSpeechIdle(): void {
    if (this.destroyed || !this.pending?.fullyQueued) return;
    this.pending = null;
    this.beginListening();
  }

  private endTurnWithError(code: string): void {
    this.queue.interrupt();
    this.pending = null;
    this.reportAndContinue(code);
  }

  private handleApiError(error: unknown): void {
    this.reportAndContinue((error as { code?: string })?.code ?? 'INTERNAL');
  }

  /** Fatal problems end the call (reported once by `fail`); the rest are shown and listening resumes. */
  private reportAndContinue(code: string): void {
    if (FATAL_CODES.has(code)) {
      this.fail(code);
      return;
    }
    this.options.callbacks.onError(code, false);
    this.beginListening();
  }

  private fail(code: string): void {
    this.cue?.stop();
    this.releaseMedia();
    this.setStatus('error');
    this.options.callbacks.onError(code, true);
  }

  private setStatus(status: VoiceStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.callbacks.onStatus(status);
  }

  // ── device and page lifecycle ────────────────────────────────────────
  private attachStream(stream: MediaStream): void {
    this.stream = stream;
    for (const track of stream.getAudioTracks?.() ?? []) {
      // Unplugged headset, revoked permission, OS took the microphone.
      track.onended = () => void this.reacquireMic();
    }
    if (this.audioContext && this.analyser) {
      this.source?.disconnect();
      this.source = this.audioContext.createMediaStreamSource(stream);
      this.source.connect(this.analyser);
    }
  }

  private async reacquireMic(): Promise<void> {
    if (this.destroyed || this.reacquiring) return;
    this.reacquiring = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      if (this.destroyed) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const old = this.stream;
      this.discardRecorder();
      this.attachStream(stream);
      old?.getTracks().forEach((track) => {
        track.onended = null;
        track.stop();
      });
      if (this.status === 'listening') this.beginListening();
    } catch {
      this.fail('MIC_UNAVAILABLE');
    } finally {
      this.reacquiring = false;
    }
  }

  private readonly onVisibility = (): void => {
    if (this.destroyed || document.visibilityState !== 'visible') return;
    void this.audioContext?.resume().catch(() => undefined);
    const dead = this.stream?.getAudioTracks?.().some((track) => track.readyState === 'ended');
    if (dead) void this.reacquireMic();
    else if (this.status === 'listening' && this.settings.talkMode === 'auto' && this.recorder?.state === 'inactive') {
      this.restartRecorder(); // the browser stopped recording while the page was hidden
    }
  };

  private releaseMedia(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    document.removeEventListener('visibilitychange', this.onVisibility);
    void this.wakeLock.release();
    this.discardRecorder();
    this.stream?.getTracks().forEach((track) => {
      track.onended = null;
      track.stop();
    });
    this.stream = null;
    this.source?.disconnect();
    this.source = null;
    if (this.audioContext) this.audioContext.onstatechange = null;
    void this.audioContext?.close().catch(() => undefined);
    this.audioContext = null;
    this.analyser = null;
  }
}

// ── browser helpers ────────────────────────────────────────────────────

export function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Chunked so a long phrase cannot overflow the call stack of `String.fromCharCode`. */
async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function playBlob(blob: Blob, signal: AbortSignal, speed: () => number): Promise<void> {
  const audio = getSharedAudio();
  const url = URL.createObjectURL(blob);
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      audio.onended = null;
      audio.onerror = null;
      signal.removeEventListener('abort', onAbort);
      URL.revokeObjectURL(url);
    };
    const onAbort = () => {
      audio.pause();
      cleanup();
      resolve();
    };
    audio.onended = () => {
      cleanup();
      resolve();
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error('Audio playback failed'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    audio.src = url;
    // Setting `src` resets the rate in some browsers, so it is applied after loading starts.
    audio.defaultPlaybackRate = speed();
    audio.playbackRate = speed();
    audio.preservesPitch = true;
    audio.play().catch((error: unknown) => {
      cleanup();
      reject(error);
    });
  });
}

function speakWithBrowserVoice(text: string, language: string, speed: number, signal: AbortSignal): Promise<void> {
  const synth = window.speechSynthesis;
  if (!synth || typeof SpeechSynthesisUtterance === 'undefined') return Promise.resolve();
  return new Promise<void>((resolve) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.rate = speed;
    const finish = () => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    };
    const onAbort = () => {
      synth.cancel();
      finish();
    };
    utterance.onend = finish;
    utterance.onerror = finish;
    signal.addEventListener('abort', onAbort, { once: true });
    synth.speak(utterance);
  });
}
